"use client"

import Image from "next/image"

import { cn } from "@/lib/utils"
import {
  getSidebarProducts,
  type ProductRecommendation,
} from "@/data/guide-product-recommendations"
import { grassTypeToGuideSlug, grassTypeToFallbackSlug } from "@/lib/grass-slug"
import { contentAnalytics } from "@/lib/analytics"
import { useAffiliateImpression } from "@/hooks/use-affiliate-impression"
import { withAmazonTagForPlacement } from "@/lib/affiliate"

interface ResultProductPicksProps {
  grassType: string
  className?: string
}

export function ResultProductPicks({ grassType, className }: ResultProductPicksProps) {
  const exactSlug = grassTypeToGuideSlug(grassType)
  const slug = exactSlug ?? grassTypeToFallbackSlug(grassType)
  const products = getSidebarProducts(slug).slice(0, 3)

  if (products.length === 0) return null

  const headline = exactSlug
    ? `Top picks for ${grassType}`
    : 'Top picks for your lawn'

  const subline = exactSlug
    ? 'Hand-checked against the tasks in your plan this month.'
    : 'General-purpose picks that fit most lawns this season.'

  return (
    <section
      className={cn("w-full", className)}
      aria-labelledby="result-product-picks-heading"
    >
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <h2
            id="result-product-picks-heading"
            className="text-base md:text-lg font-bold text-gray-900 leading-tight"
          >
            {headline}
          </h2>
          <p className="text-[12.5px] text-gray-500 mt-0.5">{subline}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        {products.map((product, i) => (
          <ProductPickCard key={`${product.name}-${i}`} product={product} grassType={grassType} position={i + 1} />
        ))}
      </div>
    </section>
  )
}

function ProductPickCard({
  product,
  grassType,
  position,
}: {
  product: ProductRecommendation
  grassType: string
  position: number
}) {
  const trackingSlug = `result-page:${grassType}`
  const asin = product.affiliateUrl.match(/\/dp\/([A-Z0-9]{10})/)?.[1]
  const href = withAmazonTagForPlacement(product.affiliateUrl, 'result-page-picks')
  // Impression + unified click on surface 'result-page-picks' (matching the
  // shared ProductPickCard) so this post-analysis peak surface's CTR is
  // computable. Fires alongside the legacy affiliate_link_clicked.
  const impressionRef = useAffiliateImpression('result-page-picks', {
    article_slug: trackingSlug,
    product_name: product.name,
    affiliate_link: href,
    asin,
    placement: 'result-page-picks',
    position,
  })

  const handleClick = () => {
    contentAnalytics.affiliateLinkClicked(
      trackingSlug,
      product.name,
      href,
      'result-page-picks',
      asin,
      { position },
    )
    contentAnalytics.affiliateProductClicked('result-page-picks', {
      productName: product.name,
      affiliateLink: href,
      asin,
      article_slug: trackingSlug,
      placement: 'result-page-picks',
      position,
    })
  }

  return (
    <a
      ref={impressionRef}
      href={href}
      target="_blank"
      rel="noopener noreferrer nofollow sponsored"
      data-affiliate="true"
      data-affiliate-placement="result-page-picks"
      onClick={handleClick}
      className="group flex flex-col rounded-xl border border-gray-200 bg-white p-3 md:p-4 hover:border-green-300 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.99] transition-all duration-200 min-w-0"
    >
      {/* Header row: product thumbnail (recognition cue) + category/name.
          Thumbnail is gated on imageUrl so search-fallback picks (no image)
          degrade to the original text-only layout with no empty box. */}
      <div className="flex items-start gap-3">
        {product.imageUrl && (
          <div className="flex-shrink-0 w-16 h-16 rounded-lg border border-gray-200 bg-linear-to-br from-gray-50 to-gray-100 overflow-hidden">
            <Image
              src={product.imageUrl}
              alt={product.name}
              width={128}
              height={128}
              loading="lazy"
              sizes="64px"
              className="w-full h-full object-contain p-1"
            />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-green-700 mb-1">
            {product.category}
          </div>
          <div className="font-semibold text-[14px] text-gray-900 leading-snug line-clamp-2">
            {product.name}
          </div>
        </div>
      </div>
      <p className="text-[12.5px] text-gray-600 leading-snug line-clamp-2 mt-2 mb-3 flex-1">
        {product.description}
      </p>
      <div className="flex items-center justify-between mt-auto">
        <span className="inline-flex items-center gap-0.5 text-[12px] font-semibold text-green-700 group-hover:text-green-800 transition-colors">
          Check price on Amazon
          <span aria-hidden="true" className="transition-transform duration-200 group-hover:translate-x-0.5">→</span>
        </span>
      </div>
    </a>
  )
}
